import { useState, useEffect, useRef } from 'react';
import {
  SelectItemProps,
  SelectListPosition,
  SelectProps,
  SelectSize,
} from '../select/types';
import OptionsList from './options-list';
import SelectedValue from './selected-value';
import IconArrowDown from '../icons/down';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import useAutocomplete from './use-autocomplete';
import useFilteredList from '../autocomplete/hooks/use-filtered-list';
import usePrevious from '../../hooks/use-previous';

import styles from './autocomplete-multiple.module.css';

export default function AutocompleteMultiple({
  disabled = false,
  list = [],
  onChange,
  onSearch,
  placeholder = '',
  title = '',
  name = '',
  className = '',
  value,
  size = SelectSize.MEDIUM,
  isLoading = false,
  borderless = false,
  selectWrapperClassName = '',
  selectListPosition = SelectListPosition.BOTTOM,
  fullWidth = false,
  error = '',
  withoutSelectedValues = false,
  contentButtons,
  tabIndex,
  selectedValuesList,
  withValuesScroll,
  withInfiniteScroll,
  onInfinateScroll,
  nextPageIsLoading,
  hasNextPage,
  optionListClassName,
  withSuffix,
}: SelectProps): React.JSX.Element {
  const ulRef = useRef<HTMLUListElement | null>(null);

  const [selectValues, setSelectedValues] = useState<string[]>([]);
  const [selectedNode, setSelectedNode] = useState(0);

  const {
    isOptionsListVisible,
    setOptionsListVisible,
    searchQuery,
    handleSearch,
    onSelectChange,
  } = useAutocomplete({
    value,
    onSearch,
    onChange,
    name,
    search: '',
  });

  const prevOpen = usePrevious(isOptionsListVisible);

  const filteredList = useFilteredList({
    list,
    searchQuery,
    isOptionsListVisible,
    autocompleteMultiple: true,
  });

  const handleSelectValue = (item: SelectItemProps) => {
    let nextSelectedValues = ([] as string[]).concat(selectValues);
    const itemIsSelected = selectValues.some(
      (selectedItem) => selectedItem.toString() === item.value.toString(),
    );

    if (itemIsSelected) {
      nextSelectedValues = nextSelectedValues.filter(
        (selectedItem) => selectedItem !== item.value,
      );
    } else {
      nextSelectedValues.push(item.value.toString());
    }

    setSelectedValues(nextSelectedValues);

    if (onSelectChange) {
      onSelectChange({
        target: {
          name,
          value: nextSelectedValues,
        },
      });
    }
  };

  const onScroll = (e: any) => {
    const nodes = ulRef?.current?.querySelectorAll('li');

    if (nodes) {
      const firstNode = nodes[0];
      const elHeight = firstNode.clientHeight;

      const node = nodes[Math.round(e.target.scrollTop / elHeight)];

      if (node) {
        // @ts-ignore
        const currentIndex = [].indexOf.call(nodes, node);

        if (currentIndex && currentIndex !== undefined && currentIndex !== -1) {
          const prevNode = nodes[selectedNode];
          const currentNode = nodes[currentIndex];

          prevNode?.classList?.remove(styles.hovered);
          currentNode?.classList.add(styles.hovered);

          setSelectedNode(currentIndex);
        }
      }
    }

    if (withInfiniteScroll && onInfinateScroll && !nextPageIsLoading) {
      const { scrollTop } = e.target;

      if (ulRef && ulRef.current) {
        const { scrollHeight, clientHeight } = ulRef.current;

        if (hasNextPage && scrollTop >= scrollHeight - clientHeight - 20) {
          onInfinateScroll();
        }
      }
    }
  };

  const onSelectNode = ({
    node,
    selected,
  }: {
    node: HTMLLIElement;
    selected: number;
  }) => {
    if (node) {
      node.classList.add(styles.hovered);

      const elHeight = node.clientHeight;
      const scrollTop = ulRef?.current?.scrollTop;

      if (scrollTop !== undefined && ulRef?.current) {
        const ulHeight = ulRef.current.clientHeight;
        const viewport = scrollTop + ulHeight;

        const elOffset = elHeight * selected;

        if (elOffset < scrollTop || elOffset + elHeight > viewport) {
          if (selectedNode !== 1) {
            ulRef?.current.scrollTo(0, elOffset);
          } else {
            ulRef?.current.scrollTo(0, 0);
          }
        }
      }
    }
  };

  const onEnter = () => {
    filteredList.forEach((item, index) => {
      if (index === selectedNode) {
        handleSelectValue(item);
      }
    });
  };

  const onUlKeydown = (e: { code: string; preventDefault: () => void }) => {
    const nodes = ulRef?.current?.querySelectorAll('li');
    let nextNode;

    if (nodes) {
      if (e.code === 'ArrowDown' && nodes[selectedNode + 1]) {
        setSelectedNode(selectedNode + 1);
        nextNode = selectedNode + 1;
        onSelectNode({
          node: nodes[selectedNode + 1],
          selected: selectedNode + 1,
        });
      }

      if (e.code === 'ArrowUp' && selectedNode !== 0) {
        nextNode = selectedNode - 1;

        setSelectedNode(selectedNode - 1);
        onSelectNode({
          node: nodes[selectedNode - 1],
          selected: selectedNode - 1,
        });
      }

      if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        const prevLi = nodes[selectedNode];

        if (prevLi && nodes[nextNode as number]) {
          prevLi.classList.remove(styles.hovered);
        }
      }

      e.preventDefault();
    }
  };

  const removeItem = (item: string | number) => {
    const nextSelectedValues = selectValues.filter(
      (selectedItem) => selectedItem !== item,
    );

    setSelectedValues(nextSelectedValues);

    if (onSelectChange) {
      onSelectChange({
        target: {
          name,
          value: nextSelectedValues,
        },
      });
    }
  };

  useEffect(() => {
    if (ulRef && ulRef.current && isOptionsListVisible) {
      const nodes = ulRef.current.querySelectorAll('li');

      if (nodes[0]) {
        nodes[0].classList.add(styles.hovered);
      }
    }
  }, [ulRef, isOptionsListVisible]);

  useEffect(() => {
    if (value) {
      const nextValues = value as string[];

      setSelectedValues(nextValues);
    } else if (!value && selectValues.length > 0) {
      setSelectedValues([]);
    }
  }, [value]);

  useEffect(() => {
    if (!isOptionsListVisible && prevOpen) {
      setSelectedNode(0);

      if (onSearch) {
        onSearch('');
      }

      ulRef?.current?.removeEventListener('keydown', () => {});
    }
  }, [ulRef, isOptionsListVisible, prevOpen]);

  return (
    <ClickOutsideDetector
      setOptionsListVisible={setOptionsListVisible}
      className={className}
    >
      <div
        className={`${styles.autocomplete} ${
          disabled ? styles.disabled : ''
        } ${className} ${size === SelectSize.SMALL ? styles.small : ''}
        ${fullWidth ? styles.fullWidth : ''}`}
      >
        {title && <div className={styles.autocompleteTitle}>{title}</div>}

        <div className={styles.autocompleteContainer}>
          <input
            type="text"
            tabIndex={tabIndex}
            className={styles.autocompleteInputSearch}
            onChange={(e) => {
              handleSearch(e);
              setSelectedNode(0);
            }}
            value={searchQuery ?? ''}
            onKeyDown={(e) => {
              if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
                onUlKeydown(e);
              }

              if (e.code === 'Enter') {
                onEnter();
              }

              if (e.code === 'Tab') {
                setOptionsListVisible(false);
              }
            }}
            onFocus={() => setOptionsListVisible(true)}
            placeholder={placeholder}
            autoComplete="off"
          />

          <div
            className={`${styles.select} ${
              isOptionsListVisible ? styles.toggled : ''
            } ${borderless ? styles.selectBorderless : ''} ${
              error ? styles.error : ''
            } ${selectWrapperClassName}`}
          >
            <button
              type="button"
              className={styles.toggleButton}
              aria-label="Toggle"
              onClick={() => setOptionsListVisible((prev) => !prev)}
              tabIndex={-1}
            />
            <button
              type="button"
              className={styles.iconWrapper}
              aria-label="Toggle"
              onClick={(e) => {
                e.stopPropagation();
                setOptionsListVisible((prev) => !prev);
              }}
              tabIndex={-1}
            >
              <IconArrowDown
                className={`${styles.arrow} ${
                  isOptionsListVisible ? styles.rotate : ''
                }`}
              />
            </button>
            {isOptionsListVisible && (
              <OptionsList
                list={filteredList}
                selectValues={selectValues}
                handleSelectValue={handleSelectValue}
                selectListPosition={selectListPosition}
                contentButtons={contentButtons}
                ulRef={ulRef}
                onScroll={onScroll}
                isLoading={isLoading}
                nextPageIsLoading={nextPageIsLoading}
                optionListClassName={optionListClassName}
              />
            )}
          </div>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        {!selectedValuesList &&
          Boolean(selectValues.length) &&
          !withoutSelectedValues && (
            <SelectedValue
              selectValues={selectValues}
              list={list}
              removeItem={removeItem}
              withValuesScroll={withValuesScroll}
              withSuffix={withSuffix}
            />
          )}
        {selectedValuesList &&
          Boolean(selectValues.length) &&
          !withoutSelectedValues && (
            <SelectedValue
              selectValues={selectValues}
              list={selectedValuesList}
              removeItem={removeItem}
              withValuesScroll={withValuesScroll}
              withSuffix={withSuffix}
            />
          )}
      </div>
    </ClickOutsideDetector>
  );
}
